import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Phone, MapPin, CheckCircle, Edit2, Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

/**
 * JobBlock - Individual job card displayed in calendar
 * 
 * Features:
 * - Draggable for rescheduling
 * - Color-coded by status with letter badge
 * - Quick action buttons
 * - Compact and full view modes
 */

const STATUS_COLORS = {
  Quoted: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    accent: 'bg-amber-400',
    letter: 'Q',
    badgeBg: 'bg-amber-500',
  },
  Scheduled: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    accent: 'bg-blue-400',
    letter: 'S',
    badgeBg: 'bg-blue-500',
  },
  'In Progress': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    accent: 'bg-purple-400',
    letter: 'I',
    badgeBg: 'bg-purple-500',
  },
  Completed: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    accent: 'bg-green-400',
    letter: 'C',
    badgeBg: 'bg-green-500',
  },
  Cancelled: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-500',
    accent: 'bg-gray-400',
    letter: 'X',
    badgeBg: 'bg-gray-500',
  },
};

/**
 * Parse time from scheduledDate - your system stores LOCAL time, not UTC
 * So "2026-01-09T08:00:00" means 8:00 AM local
 */
const parseJobTime = (dateString) => {
  if (!dateString) return null;
  
  // Extract time portion directly from the ISO string
  const timePart = dateString.split('T')[1];
  if (!timePart) return null;
  
  const [hours, minutes] = timePart.split(':');
  const h = parseInt(hours);
  const m = parseInt(minutes);
  
  // Check if midnight (no time set)
  if (h === 0 && m === 0) {
    return null;
  }
  
  // Format for display
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const JobBlock = ({
  job,
  compact = false,
  isDragging = false,
  onComplete,
  onEdit,
  onCall,
  onNavigate,
  onClick,
  isOwner = true,
  disableDrag = false,
}) => {
  const colors = STATUS_COLORS[job.status] || STATUS_COLORS.Scheduled;

  // Get service names
  const serviceNames = job.jobServices
    ?.map(js => js.serviceType?.name)
    .filter(Boolean)
    .join(', ') || job.serviceType || '';

  const time = parseJobTime(job.scheduledDate);
  const canComplete = job.status === 'Scheduled' || job.status === 'In Progress';

  // Draggable setup - only if drag is enabled
  const dragEnabled = !disableDrag && job.status !== 'Completed' && job.status !== 'Cancelled';
  
  const { attributes, listeners, setNodeRef, transform, isDragging: isCurrentlyDragging } = useDraggable({
    id: `job-${job.id}`,
    data: { job },
    disabled: !dragEnabled,
  });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: 1000,
    opacity: isCurrentlyDragging ? 0.8 : 1,
  } : undefined;

  // Handle click - only fire if not dragging
  const handleClick = (e) => {
    // Don't fire click if we were dragging
    if (isCurrentlyDragging) return;
    e.stopPropagation();
    onClick?.(job);
  };

  // Compact view for week columns
  if (compact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(dragEnabled ? listeners : {})}
        className={`
          relative ${colors.bg} ${colors.border} border rounded-lg p-2 mb-1.5
          cursor-pointer hover:shadow-md transition-shadow
          ${isCurrentlyDragging ? 'shadow-lg ring-2 ring-primary-400' : ''}
          ${dragEnabled ? 'touch-none' : ''}
        `}
      >
        {/* Clickable overlay - separate from drag */}
        <div 
          className="absolute inset-0 z-10" 
          onClick={handleClick}
        />
        
        {/* Status Badge - Top Right */}
        <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 ${colors.badgeBg} rounded-full flex items-center justify-center shadow-sm z-20`}>
          <span className="text-white text-[10px] font-bold">{colors.letter}</span>
        </div>

        {/* Time badge */}
        {time && (
          <div className={`text-xs font-medium ${colors.text} mb-1`}>
            {time}
          </div>
        )}
        
        {/* Customer name */}
        <div className="font-medium text-gray-900 text-sm truncate pr-4">
          {job.customer?.name || 'Unknown'}
        </div>
        
        {/* Amount - Owner only */}
        {isOwner && job.quoteAmount > 0 && (
          <div className={`text-xs font-semibold ${colors.text} mt-0.5`}>
            {formatCurrency(job.quoteAmount)}
          </div>
        )}
        
        {/* Status indicator line */}
        <div className={`${colors.accent} h-0.5 w-full rounded-full mt-2`} />
      </div>
    );
  }

  // Full view for day agenda
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(dragEnabled ? listeners : {})}
      className={`
        relative ${colors.bg} ${colors.border} border rounded-xl p-3
        ${isCurrentlyDragging ? 'shadow-xl ring-2 ring-primary-400' : 'shadow-sm hover:shadow-md'}
        transition-all cursor-pointer
        ${dragEnabled ? 'touch-none' : ''}
      `}
    >
      {/* Clickable overlay for opening detail - separate from drag */}
      <div 
        className="absolute inset-0 z-10 rounded-xl" 
        onClick={handleClick}
      />

      {/* Status Badge - Top Right */}
      <div className={`absolute -top-2 -right-2 w-6 h-6 ${colors.badgeBg} rounded-full flex items-center justify-center shadow z-20`}>
        <span className="text-white text-xs font-bold">{colors.letter}</span>
      </div>

      {/* Header row */}
      <div className="flex items-start justify-between gap-2 pr-4">
        <div className="flex-1 min-w-0">
          {/* Time */}
          {time && (
            <div className={`flex items-center gap-1 text-xs font-medium ${colors.text} mb-1`}>
              <Clock size={12} />
              {time}
            </div>
          )}
          
          {/* Customer name */}
          <div className="font-semibold text-gray-900 truncate">
            {job.customer?.name || 'Unknown'}
          </div>
          
          {/* Address */}
          <div className="flex items-center gap-1 text-sm text-gray-500 truncate mt-0.5">
            <MapPin size={12} className="flex-shrink-0" />
            <span className="truncate">{job.address}</span>
          </div>
          
          {/* Service type */}
          {serviceNames && (
            <div className="text-sm text-gray-600 truncate mt-1">
              {serviceNames}
            </div>
          )}
        </div>

        {/* Amount - Owner only */}
        {isOwner && job.quoteAmount > 0 && (
          <div className={`text-lg font-bold ${colors.text} flex-shrink-0`}>
            {formatCurrency(job.quoteAmount)}
          </div>
        )}
      </div>

      {/* Quick action buttons */}
      <div className="flex items-center gap-1 mt-3 pt-2 border-t border-gray-200/50">
        {/* Call */}
        {job.customer?.phone && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCall?.(job);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
          >
            <Phone size={14} />
            <span className="hidden sm:inline">Call</span>
          </button>
        )}

        {/* Navigate */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate?.(job);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
        >
          <MapPin size={14} />
          <span className="hidden sm:inline">Navigate</span>
        </button>

        {/* Edit */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(job);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
        >
          <Edit2 size={14} />
          <span className="hidden sm:inline">Edit</span>
        </button>

        {/* Complete */}
        {canComplete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onComplete?.(job);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <CheckCircle size={14} />
            <span className="hidden sm:inline">Complete</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default JobBlock;